import { memo, useCallback, useEffect, useMemo, useState } from 'react';
// Types
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
// Slices
import {
  checkFirstPaddingRuleExist,
  createPaddingRule,
  deletePaddingRule,
  duplicatePaddingRule,
  paddingRuleMapSelector,
  paddingRulesLoading,
  paddingRulesSelector,
  selectedIdsSelector,
  setFirstPaddingRule,
  setSecondPaddingRule,
} from 'slices/paddingRules.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
// Utils
import { matchString } from 'utils/stringUtils';
import { EPaddingRuleType, type TPaddingRule } from '@timeedit/types';
// Components
import {
  Button,
  EditableTable,
  Filter,
  TEEFilterType,
  type TEIFilter,
  type TEIFilterField,
  type TEIOption,
} from '@timeedit/ui-components';
import { Dropdown, type MenuProps, Space } from '@timeedit/ui-components/antd';
// Styles
import './PaddingRuleList.scss';

export interface IRowData {
  id: number;
  name: string;
  type: string;
}

interface Props {
  confirmDiscardPaddingChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const PaddingRuleList = ({ confirmDiscardPaddingChanges }: Props) => {
  const dispatch = useAppDispatch();

  const paddingRules: TPaddingRule[] = useAppSelector(paddingRulesSelector);
  const paddingRuleMap: Record<number, TPaddingRule> = useAppSelector(
    paddingRuleMapSelector
  );
  const [highlightIds, setHighlightIds] = useState<number[]>([]);
  const selectedIds: number[] = useAppSelector(selectedIdsSelector);

  useEffect(() => {
    setTimeout(() => {
      setHighlightIds(selectedIds);
    }, 100);
  }, [selectedIds.join('-')]);

  const loading = useAppSelector(paddingRulesLoading);

  const [filterSettings, setFilterSettings] = useState<TEIFilter>({
    filters: {},
    freeTextSearch: '',
  });

  const [confirmDelete, contextHolder] = useDeleteConfirmation();

  const filterData: IRowData[] = useMemo(() => {
    const dataSource = paddingRules.map((padding) => ({
      id: padding.id,
      name: padding.name || 'New padding rule',
      type: getText(padding.paddingType),
      paddingType: padding.paddingType,
    }));

    return dataSource.filter((pr) => {
      let matchedType = true;
      let matchedText = true;
      const {
        filters: { type },
        freeTextSearch,
      } = filterSettings;
      if (type) {
        matchedType = pr.paddingType === type;
      }
      if (freeTextSearch) {
        matchedText = matchString(pr.name, freeTextSearch);
      }
      return matchedType && matchedText;
    });
  }, [paddingRules, filterSettings]);

  const onCreate = useCallback((paddingRuleType: EPaddingRuleType) => {
    confirmDiscardPaddingChanges(true, () =>
      dispatch(createPaddingRule(paddingRuleType))
    );
  }, []);

  const onDuplicate = useCallback(
    (record: IRowData) => {
      confirmDiscardPaddingChanges(true, () =>
        dispatch(duplicatePaddingRule(paddingRuleMap[record.id]))
      );
    },
    [paddingRuleMap]
  );

  const onCompare = useCallback(
    (record: IRowData) => {
      const firstPaddingRuleExist = dispatch(checkFirstPaddingRuleExist());
      if (!firstPaddingRuleExist) {
        return dispatch(setFirstPaddingRule(paddingRuleMap[record.id]));
      }
      confirmDiscardPaddingChanges(false, () =>
        dispatch(setSecondPaddingRule(paddingRuleMap[record.id]))
      );
    },
    [paddingRuleMap]
  );

  const onRowClick = useCallback(
    (record: IRowData) => {
      confirmDiscardPaddingChanges(true, () =>
        dispatch(setFirstPaddingRule(paddingRuleMap[record.id]))
      );
    },
    [paddingRuleMap]
  );

  const onRow = useRowProps(onRowClick, highlightIds);

  const onDelete = useCallback(
    (record: IRowData) =>
      confirmDelete(record.name, () => dispatch(deletePaddingRule(record.id))),
    [confirmDelete, dispatch]
  );

  const createNewMenu: MenuProps = useMemo(
    () => ({
      items: [
        {
          key: EPaddingRuleType.DIRECT,
          label: getText(EPaddingRuleType.DIRECT),
        },
        {
          key: EPaddingRuleType.TRAVEL_TIME,
          label: getText(EPaddingRuleType.TRAVEL_TIME),
        },
      ],
      onClick: (info) => {
        onCreate(info.key as EPaddingRuleType);
      },
    }),
    [onCreate]
  );

  const checkDrawerOpen = useCallback(
    () => Boolean(dispatch(checkFirstPaddingRuleExist())),
    [dispatch]
  );

  const menuProps = useCallback(
    (record: IRowData) =>
      getMenuProps<IRowData>({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen,
        title: record.name,
      }),
    [onCompare, onDuplicate, onDelete, checkDrawerOpen]
  );

  const footer = useCallback(
    () => (
      <Space>
        <Dropdown menu={createNewMenu} trigger={['click']}>
          <Button size="small" type="primary" onClick={() => {}}>
            Create new
          </Button>
        </Dropdown>
      </Space>
    ),
    [createNewMenu]
  );

  const title = useTableTitle<IRowData>({
    total: paddingRules.length,
    filteredAmount: filterData.length,
  });

  const [contentRef, defaultTableProps] = useDefaultTableProps();

  return (
    <div className="padding-rule-list" ref={contentRef}>
      <Filter
        defaultFields={defaultFields}
        options={options}
        settings={filterSettings}
        onChange={setFilterSettings}
      />
      <LoadingWrapper isLoading={loading}>
        <EditableTable
          dataSource={filterData}
          columns={columns}
          getMenuProps={menuProps}
          onRow={onRow}
          title={title}
          footer={footer}
          {...defaultTableProps}
        />
      </LoadingWrapper>
      {contextHolder}
    </div>
  );
};

export default memo(PaddingRuleList);

const columns = [
  {
    key: 'name',
    dataIndex: 'name',
    title: 'Name',
    ellipsis: true,
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.name.localeCompare(item2.name),
  },
  {
    key: 'type',
    dataIndex: 'type',
    title: 'Padding type',
    ellipsis: true,
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.type.localeCompare(item2.type),
  },
];

const defaultFields: TEIFilterField[] = [
  {
    label: 'Type',
    key: 'type',
    type: TEEFilterType.text,
  },
];
const options: Record<string, TEIOption[]> = {
  type: Object.values(EPaddingRuleType).map((type) => ({
    value: type,
    label: getText(type),
  })),
};
